import { useState } from "react";
import {
  Button,
  Container,
  Grid,
  Typography,
  // useMediaQuery,
  useTheme,
  TextField,
  Box,
  Paper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../../assets/logo1.png";
import { useResetPassword } from "../../hooks/query/useUser";

// Password validation schema
const resetPasswordSchema = z
  .object({
    newPassword: z.string(),
    // .min(8, { message: "Password must be at least 8 characters long" })
    // .regex(/[A-Z]/, {
    //   message: "Password must contain at least one uppercase letter",
    // })
    // .regex(/[a-z]/, {
    //   message: "Password must contain at least one lowercase letter",
    // })
    // .regex(/[0-9]/, { message: "Password must contain at least one number" })
    // .regex(/[!@#$%^&*()]/, {
    //   message: "Password must contain at least one special character",
    // }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const ResetPassword = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const resetToken = new URLSearchParams(location.search).get("resetToken");
  console.log("Reset token", resetToken);
  if (!resetToken) {
    throw new Error("token not found");
  }
  // const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { mutateAsync: resetPassword } = useResetPassword();

  const onSubmit = (data: any) => {
    const formData = { resetToken, newPassword: data.newPassword };
    resetPassword(formData, {
      onSuccess: () => {
        toast.success("Password reset successfully!");
        navigate("/login");
      },
      onError: (error: any) => {
        const errorMessage =
          error?.body?.error?.message ||
          "Failed to reset password. Please try again.";
        toast.error(errorMessage);
      },
    });
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: "450px",
          borderRadius: theme.spacing(3),
          padding: theme.spacing(4),
          background: "linear-gradient(145deg, #f0f4f8 0%, #e6eaf0 100%)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            left: "-50px",
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #bb0000 0%, #bb0000 100%)",
            opacity: 0.1,
          }}
        />
        <Box
          sx={{
            textAlign: "center",
            marginBottom: theme.spacing(3),
          }}
        >
          <img
            src={logo}
            alt="logo"
            style={{
              width: "200px",
              height: "200px",
              marginBottom: theme.spacing(2),
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
            }}
          >
            Reset Password
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Create a new secure password
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="New Password"
                    type={showNewPassword ? "text" : "password"}
                    variant="outlined"
                    error={Boolean(errors.newPassword)}
                    helperText={errors.newPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: theme.spacing(2),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    variant="outlined"
                    error={Boolean(errors.confirmPassword)}
                    helperText={errors.confirmPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: theme.spacing(2),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  mt: 2,
                  mb: 2,
                  borderRadius: theme.spacing(2),
                  padding: theme.spacing(1.5),
                  background:
                    "linear-gradient(45deg, #bb0000 0%, #bb0000 100%)",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: "0 6px 8px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default ResetPassword;
